import React from 'react';
import produce from 'immer';
import { Button } from '@saleshandy/design-system';
import { withTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import type { IProps, IState } from './reply-forward-editor-container';
import { validate as rawValidate } from '../../../sequence/shared/modals/email-modal/validator';

import EmailInput from '../email-input';
import Editor from '../../../../shared/editor';
import TemplateModal from '../../../sequence/shared/modals/template-modal';
import ConfirmationModalV3 from '../../../../shared/design-system/components/atoms/confirmation-modal/confirmation-modal-v3';
import { ConfirmationModalIconType } from '../../../../shared/design-system/components/atoms/confirmation-modal/enum';
import {
  attachmentStatusChangeHandler,
  prepareMergeTagOptions,
} from '../../../sequence/shared/modals/email-modal/helpers';
import {
  AttachmentMetaDataType,
  AttachmentType,
  AttachmentUploadStatus,
} from '../../../../shared/editor/types';
import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatusWithPrevStatusCheck,
} from '../../../../shared/utils/execute-on-request-status';
import toaster, { Theme } from '../../../../shared/toaster';
import { getIsRequestPending } from '../../../../shared/utils/get-request-status';
import { getFormattedAttachmentsData } from '../../../sequence/shared/modals/variant-modal/helper';
import { getDefaultContentForReplyForwardEditor } from '../../helpers/utils/get-default-content-for-reply-forward-editor';
import { getDefaultValueForToField } from '../../components/unified-inbox-mails-content/utils/get-default-value-for-to-field';
import { getIsErrorInReplyForwardEditor } from '../../components/unified-inbox-mails-content/utils/get-is-error-in-reply-forward-editor';
import { getDefaultValueForCcBccField } from '../../components/unified-inbox-mails-content/utils/get-default-value-for-cc-bcc-field';
import ScheduleReplyModal from '../../../../shared/components/schedule-reply-modal';
import { RequestStatus } from '../../../../shared/enums/request-status';
import {
  ReplyDraftPayload,
  DraftStatus,
  UnifiedScheduleAttachment,
  ReplySchedulePayload,
} from '../../type';
import { ScheduleReplyData } from '../../../../shared/components/schedule-reply-modal/types';
import { generateRandomString } from '../../../../shared/utils';
import { Variant } from '../../../../shared/design-system/components/atoms/button/button';
import { getIsFeedDraft } from '../../helpers/helper';
import {
  isValidHTML,
  nl2br,
} from '../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-merge-tag-mapping-with-values';
import GlobalRestrictionModal from '../../../home/components/global-restriction-modal';
import {
  dateChipTags,
  insertChipForDateTag,
  insertChipForMergeTag,
  setCaretAfterChip,
  stripChipTagsFromHtml,
} from '../../../../shared/editor/helpers';
import SingleEditorInput from '../../../sequence/shared/modals/email-modal/components/single-editor-input';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';

class ReplyForwardEditor extends React.PureComponent<IProps, IState> {
  private readonly editorRef;

  // Debounce the save draft calls.
  debouncedDraft = debounce(() => {
    this.setState({ draftStatus: DraftStatus.Ready });
    this.setState({ isEditorContentUnsaved: false });
  }, 3000);

  // Debouncing "Draft Auto Saved" text blinking behavior
  debouncedDraftSavedText = debounce(() => {
    this.setState({ isDraftSavedTextVisible: false });
  }, 3000);

  defaultToInputValue = [];

  constructor(props: IProps) {
    super(props);

    const { action, selectedEmail, unifiedSchedule } = this.props;
    const defaultCc = getDefaultValueForCcBccField(action, selectedEmail, 'cc');
    const defaultBcc = getDefaultValueForCcBccField(
      action,
      selectedEmail,
      'bcc',
    );
    const isCCInputVisible =
      unifiedSchedule?.cc?.length > 0 || defaultCc.length > 0;
    const isBCCInputVisible =
      unifiedSchedule?.bcc?.length > 0 || defaultBcc.length > 0;
    this.defaultToInputValue = getDefaultValueForToField(action, selectedEmail);

    // Preparing Attachment Array from
    let draftAttachmentSizeInBytes = 0;
    let draftAttachments = unifiedSchedule?.attachments?.map(
      (item): AttachmentType => {
        draftAttachmentSizeInBytes += Number(item.size);
        return {
          attachmentId: item.id,
          status: AttachmentUploadStatus.UploadSuccess,
          attachmentSize: Number(item.size),
          attachmentIdentifier: generateRandomString(6),
          file: {
            name: item.name,
            size: item.size,
            type: item.type,
          },
        };
      },
    );

    // Preparing attachment in forward email
    if (action === 'forward') {
      draftAttachments = selectedEmail?.attachments?.map(
        (item): AttachmentType => {
          draftAttachmentSizeInBytes += Number(item.size);
          return {
            attachmentId: item.id,
            status: AttachmentUploadStatus.UploadSuccess,
            attachmentSize: Number(item.size),
            attachmentIdentifier: generateRandomString(6),
            file: {
              name: item.name,
              size: item.size,
              type: item.type,
            },
          };
        },
      );
    }

    this.state = {
      isTemplateApplied: false,
      values: {
        to: unifiedSchedule?.to ? unifiedSchedule.to : this.defaultToInputValue,
        cc: unifiedSchedule?.cc ? unifiedSchedule.cc : defaultCc,
        bcc: unifiedSchedule?.bcc ? unifiedSchedule.bcc : defaultBcc,
        content: getDefaultContentForReplyForwardEditor(
          action,
          selectedEmail,
          unifiedSchedule?.content,
        ),
      },
      errors: {
        to: '',
        cc: '',
        bcc: '',
        content: '',
      },
      dirty: {
        to: false,
        cc: false,
        bcc: false,
        content: false,
        attachments: false,
      },
      temporaryInputValues: {
        to: '',
        cc: '',
        bcc: '',
      },

      isCCInputVisible,
      isBCCInputVisible,
      showTemplates: false,

      selectedTemplate: null,

      showConfirmationModal: false,
      showScheduleModal: false,

      attachments: draftAttachments || [],
      attachmentsMeta: [],
      attachmentsSizeInBytes: draftAttachmentSizeInBytes || 0,

      isDraftSavePending: false,
      draftStatus: DraftStatus.Idle,
      isDraftSavedTextVisible: false,
      isEditorContentUnsaved: false,
      showGlobalRestrictionModal: false,
      globalRestrictionModalContent: {
        title: '',
        description: '',
      },
    };

    this.editorRef = React.createRef();
    this.onCCInputToggle = this.onCCInputToggle.bind(this);
    this.onBCCInputToggle = this.onBCCInputToggle.bind(this);
    this.validate = this.validate.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onEditorChange = this.onEditorChange.bind(this);
    this.getEditorInstance = this.getEditorInstance.bind(this);
    this.showTemplateModal = this.showTemplateModal.bind(this);
    this.hideTemplateModal = this.hideTemplateModal.bind(this);
    this.showScheduleModal = this.showScheduleModal.bind(this);
    this.hideScheduleModal = this.hideScheduleModal.bind(this);
    this.onTemplateChange = this.onTemplateChange.bind(this);
    this.onAttachmentsSizeChange = this.onAttachmentsSizeChange.bind(this);
    this.onAttachmentStatusChange = this.onAttachmentStatusChange.bind(this);
    this.onAttachmentsChange = this.onAttachmentsChange.bind(this);
    this.onAttachmentsMetaChange = this.onAttachmentsMetaChange.bind(this);
    this.onEmailSend = this.onEmailSend.bind(this);
    this.onDiscardModalShow = this.onDiscardModalShow.bind(this);
    this.getEmailThreadsRequest = this.getEmailThreadsRequest.bind(this);
    this.onTempInputChange = this.onTempInputChange.bind(this);
    this.onResetState = this.onResetState.bind(this);
    this.addMergeTagInContent = this.addMergeTagInContent.bind(this);
    this.addVariableTagInContent = this.addVariableTagInContent.bind(this);
    this.onSchedule = this.onSchedule.bind(this);
    this.sendDraftSave = this.sendDraftSave.bind(this);
    this.onEditorBlur = this.onEditorBlur.bind(this);
    this.onInputChangeForDraft = this.onInputChangeForDraft.bind(this);
    this.onDiscardDraft = this.onDiscardDraft.bind(this);
    this.onUpdateDraftStatusToIdeal = this.onUpdateDraftStatusToIdeal.bind(
      this,
    );
    this.hideGlobalRestrictionModal = this.hideGlobalRestrictionModal.bind(
      this,
    );
  }

  componentDidMount(): void {
    const { sendGetFieldsRequest } = this.props;

    sendGetFieldsRequest();
  }

  componentDidUpdate(
    prevProps: Readonly<IProps>,
    prevState: Readonly<IState>,
  ): void {
    const {
      uniboxFiltersFeed,
      replyEmailRequestStatus,
      replyEmailRequestError,

      forwardEmailRequestStatus,
      forwardEmailRequestError,

      replyScheduleRequestStatus,
      replyScheduleRequestError,
      replyDraftDiscardRequestStatus,
      replyDraftDiscardRequestError,

      resetReplyForwardDraftRequest,
      updateUniboxEmailScheduleData,

      onHideReplyEditor,
      hashId,
      action,
      selectedEmail,

      replyDraftRequestStatus,
    } = this.props;

    const { isDraftSavePending, draftStatus } = this.state;

    if (action !== prevProps.action) {
      this.onResetState();
    }

    executeOnRequestStatusWithPrevStatusCheck({
      status: replyEmailRequestStatus,
      prevStatus: prevProps.replyEmailRequestStatus,
      onSuccess: () => {
        onHideReplyEditor();
        resetReplyForwardDraftRequest(selectedEmail.emailId);
        const { shouldFetchEmailThreadsOnReplyAndForward = true } = this.props;
        if (shouldFetchEmailThreadsOnReplyAndForward) {
          toaster.success('Email has been sent successfully.', {
            theme: Theme.New,
          });
          this.getEmailThreadsRequest();
        }
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: replyEmailRequestError,
          onError: () => {
            toaster.error(replyEmailRequestError.message, { theme: Theme.New });
          },
        });
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: forwardEmailRequestStatus,
      prevStatus: prevProps.forwardEmailRequestStatus,
      onSuccess: () => {
        onHideReplyEditor();
        const { shouldFetchEmailThreadsOnReplyAndForward = true } = this.props;
        if (shouldFetchEmailThreadsOnReplyAndForward) {
          this.getEmailThreadsRequest();
        }
        toaster.success('Email has been sent successfully. ', {
          theme: Theme.New,
        });
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: forwardEmailRequestError,
          onError: () => {
            toaster.error(forwardEmailRequestError.message, {
              theme: Theme.New,
            });
          },
        });
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: replyDraftRequestStatus,
      prevStatus: prevProps.replyDraftRequestStatus,
      onSuccess: () => {
        if (isDraftSavePending) {
          this.debouncedDraft();
          this.setState({ isDraftSavePending: false });
        }
        // Showing Draft Auto Saved Blink Text
        this.setState({ isDraftSavedTextVisible: true });
        this.debouncedDraftSavedText();
        updateUniboxEmailScheduleData(
          hashId,
          getIsFeedDraft(uniboxFiltersFeed),
        );
      },
      onFailed: () => {
        if (isDraftSavePending) {
          this.debouncedDraft();
          this.setState({ isDraftSavePending: false });
        }
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: replyScheduleRequestStatus,
      prevStatus: prevProps.replyScheduleRequestStatus,
      onSuccess: () => {
        onHideReplyEditor();
        this.hideScheduleModal();
        toaster.success('Reply has been scheduled successfully.', {
          theme: Theme.New,
        });
        updateUniboxEmailScheduleData(
          hashId,
          getIsFeedDraft(uniboxFiltersFeed),
        );
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: replyScheduleRequestError,
          onError: () => {
            toaster.error(replyScheduleRequestError?.message, {
              theme: Theme.New,
            });
          },
        });
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: replyDraftDiscardRequestStatus,
      prevStatus: prevProps.replyDraftDiscardRequestStatus,
      onSuccess: () => {
        onHideReplyEditor();
        this.setState({ showConfirmationModal: false });
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: replyDraftDiscardRequestError,
          onError: () => {
            toaster.error(replyDraftDiscardRequestError?.message, {
              theme: Theme.New,
            });
          },
        });
      },
    });

    if (
      draftStatus !== prevState.draftStatus &&
      draftStatus === DraftStatus.Ready
    ) {
      this.sendDraftSave();
      this.onUpdateDraftStatusToIdeal();
    }
  }

  componentWillUnmount(): void {
    const {
      selectedEmail,
      resetReplyForwardDraftRequest,
      resetSequenceTimezoneData,
    } = this.props;
    resetReplyForwardDraftRequest(selectedEmail.emailId);
    resetSequenceTimezoneData(selectedEmail.emailId);
  }

  onUpdateDraftStatusToIdeal() {
    this.setState({ draftStatus: DraftStatus.Idle });
  }

  onResetState() {
    const { action, selectedEmail } = this.props;

    this.setState({
      values: {
        to: getDefaultValueForToField(action, selectedEmail),
        cc: [],
        bcc: [],
        content: getDefaultContentForReplyForwardEditor(action, selectedEmail),
      },
      errors: {
        to: '',
        cc: '',
        bcc: '',
        content: '',
      },
      dirty: {
        to: false,
        cc: false,
        bcc: false,
        content: false,
        attachments: false,
      },
      temporaryInputValues: {
        to: '',
        cc: '',
        bcc: '',
      },

      isCCInputVisible: false,
      isBCCInputVisible: false,
      showTemplates: false,

      selectedTemplate: null,

      showConfirmationModal: false,

      attachments: [],
      attachmentsMeta: [],
      attachmentsSizeInBytes: 0,
    });
  }

  onDiscardModalShow() {
    this.setState({ showConfirmationModal: true });
  }

  onCCInputToggle() {
    const { isCCInputVisible } = this.state;
    this.setState({ isCCInputVisible: !isCCInputVisible });
  }

  onBCCInputToggle() {
    const { isBCCInputVisible } = this.state;
    this.setState({ isBCCInputVisible: !isBCCInputVisible });
  }

  onTempInputChange(name: string, value: string) {
    this.setState(
      produce((draft) => {
        draft.temporaryInputValues[name] = value;
      }),
    );
  }

  onInputChange(name: string, value: string[]) {
    value.forEach((v) => this.validate(name, v));

    this.setState(
      produce((draft) => {
        draft.values[name] = value;
        draft.dirty[name] = true;
      }),
      () => {
        if (name === 'to') {
          const { values, errors } = this.state;
          const errorsRef = { ...errors };
          const error = this.validate('content', values.content);
          errorsRef.content = error;

          this.setState({ errors: errorsRef });
        }
      },
    );
    this.onInputChangeForDraft();
  }

  onEditorBlur() {
    const { isEditorContentUnsaved } = this.state;
    if (isEditorContentUnsaved) {
      this.onInputChangeForDraft();
      this.setState({ isEditorContentUnsaved: false });
    }
  }

  onInputChangeForDraft() {
    const { isDraftSavePending } = this.state;
    const { action } = this.props;
    if (action !== 'forward' && !isDraftSavePending) {
      this.debouncedDraft.cancel();
      this.setState({ draftStatus: DraftStatus.Ready });
    }
  }

  onEditorChange(value: string) {
    const { errors } = this.state;
    const { action } = this.props;
    const errorsRef = { ...errors };
    const error = this.validate('content', value);
    errorsRef.content = error;

    this.setState({ errors: errorsRef });

    this.setState(
      produce((draft) => {
        draft.values.content = value;
        draft.dirty.content = true;
      }),
    );
    if (action !== 'forward') {
      this.debouncedDraft();
      this.setState({ isEditorContentUnsaved: true });
    }
  }

  onTemplateChange(selectedTemplate) {
    const { values, attachments } = this.state;

    const valuesRef = { ...values };

    this.editorRef?.current?.execCommand?.(
      'mceInsertContent',
      false,
      selectedTemplate.content,
    );

    valuesRef.content =
      this.editorRef?.current?.getContent?.() || values.content;

    this.onAttachmentsChange([
      ...attachments,
      ...getFormattedAttachmentsData(
        selectedTemplate?.template?.templateAttachments,
        AttachmentUploadStatus.UploadSuccess,
      ),
    ]);

    this.setState((prevState) => ({
      ...prevState,
      values: { ...prevState.values, ...valuesRef },
      showTemplates: false,
      selectedTemplate,
      isTemplateApplied: true,
    }));
  }

  onTemplateAppliedReset = () => {
    this.setState((prevState) => ({
      ...prevState,
      isTemplateApplied: false,
    }));
  };

  onAttachmentsSizeChange(isAttachmentDeleted: boolean) {
    const { attachments, errors, values } = this.state;

    let attachmentsSizeInBytes = 0;
    attachments.forEach((item) => {
      attachmentsSizeInBytes += parseInt(item.file.size, 10);
    });

    if (!isAttachmentDeleted) {
      this.setState(
        produce((draft) => {
          draft.dirty.attachments = true;
        }),
      );
    }

    this.setState({ attachmentsSizeInBytes }, () => {
      const errorsRef = { ...errors };
      const error = this.validate('content', values.content);
      errorsRef.content = error;
      this.setState({ errors: errorsRef });
    });
  }

  onAttachmentStatusChange(attachment: AttachmentType) {
    const { attachments } = this.state;

    if (attachment.status === AttachmentUploadStatus.UploadSuccess) {
      this.onInputChangeForDraft();
    }

    this.setState(
      {
        attachments: attachmentStatusChangeHandler(attachment, attachments),
      },
      () => this.onAttachmentsSizeChange(false),
    );
  }

  onAttachmentsChange(
    updatedAttachments: AttachmentType[],
    isAttachmentDeleted = false,
  ) {
    this.setState(
      {
        attachments: [...updatedAttachments],
      },
      () => this.onAttachmentsSizeChange(isAttachmentDeleted),
    );
    if (isAttachmentDeleted) {
      this.onInputChangeForDraft();
    }
  }

  onAttachmentsMetaChange(attachmentsMeta: AttachmentMetaDataType[]) {
    this.setState({
      attachmentsMeta,
    });
  }

  onDiscardDraft() {
    const {
      selectedEmail,
      unifiedSchedule,
      uniboxFiltersFeed,
      sendReplyDraftDiscardRequest,
      action,
      onHideReplyEditor,
      hashId,
    } = this.props;
    if (action === 'forward' || !unifiedSchedule) {
      onHideReplyEditor();
      this.setState({ showConfirmationModal: false });
    } else {
      sendReplyDraftDiscardRequest({
        repliedOnEmailId: Math.abs(Number(selectedEmail.emailId)),
        hashId,
        isDraftTab: getIsFeedDraft(uniboxFiltersFeed),
      });
    }
  }

  onEmailSend() {
    const { isDemoAccount } = this.props;
    if (isDemoAccount) {
      this.setState({
        showGlobalRestrictionModal: true,
        globalRestrictionModalContent: {
          title: 'Sending emails is disabled in demo mode',
          description:
            'This is a demo account with sample data. Signup to send real emails from your Unified Inbox.',
        },
      });
      return;
    }

    this.debouncedDraft.cancel();
    const { attachments } = this.state;

    const isAnyAttachmentUploadingOrFailed = attachments.some(
      (item) =>
        item.status === AttachmentUploadStatus.UploadFailed ||
        item.status === AttachmentUploadStatus.Uploading,
    );

    if (isAnyAttachmentUploadingOrFailed) {
      return;
    }

    // Validate All

    const {
      values: { to, cc, bcc, content },
      selectedTemplate,
    } = this.state;
    const {
      action,
      selectedEmail,
      unifiedSchedule,
      sendReplyEmailRequest,
      sendForwardEmailRequest,
    } = this.props;
    const convertedContent = stripChipTagsFromHtml(content);

    const attachmentIds = attachments
      .filter((item) => item.status === AttachmentUploadStatus.UploadSuccess)
      .map((item) => item.attachmentId);

    if (action === 'reply') {
      sendReplyEmailRequest({
        emailId: Math.abs(Number(selectedEmail.emailId)),
        emailThreadId: Number(selectedEmail.emailThreadId),
        content: convertedContent,
        ...(unifiedSchedule?.unifiedScheduledId && {
          unifiedScheduledId: Math.abs(
            Number(unifiedSchedule.unifiedScheduledId),
          ),
        }),
        ...(to.length && { to }),
        ...(cc.length && { cc }),
        ...(bcc.length && { bcc }),
        ...(attachmentIds.length && { attachmentIds }),
        ...(selectedTemplate && { templateId: Number(selectedTemplate.id) }),
      });
    } else if (action === 'replyAll') {
      sendReplyEmailRequest({
        emailId: selectedEmail.emailId,
        emailThreadId: Number(selectedEmail.emailThreadId),
        content: convertedContent,
        ...(unifiedSchedule?.unifiedScheduledId && {
          unifiedScheduledId: unifiedSchedule.unifiedScheduledId,
        }),
        ...(to.length && { to }),
        ...(cc.length && { cc }),
        ...(bcc.length && { bcc }),
        ...(attachmentIds.length && { attachmentIds }),
        ...(selectedTemplate && { templateId: Number(selectedTemplate.id) }),
      });
    } else {
      sendForwardEmailRequest({
        emailId: Math.abs(Number(selectedEmail.emailId)),
        emailThreadId: Number(selectedEmail.emailThreadId),
        content: convertedContent,
        ...(to.length && { to }),
        ...(cc.length && { cc }),
        ...(bcc.length && { bcc }),
        ...(attachmentIds.length && { attachmentIds }),
        ...(selectedTemplate && { templateId: Number(selectedTemplate.id) }),
      });
    }
  }

  onSchedule(scheduleReplyData: ScheduleReplyData) {
    const {
      selectedEmail,
      unifiedSchedule,
      sendReplyScheduleRequest,
    } = this.props;
    const { date, isSendEmailIfReplied, timezone } = scheduleReplyData;

    const payload: ReplySchedulePayload = {
      repliedOnEmailId: selectedEmail.emailId,
      unifiedScheduledId: unifiedSchedule.unifiedScheduledId,
      scheduledAt: date,
      sendEmailIfReplied: isSendEmailIfReplied,
      timezone,
    };

    sendReplyScheduleRequest(payload);
  }

  getEmailThreadsRequest() {
    const { hashId, sendGetEmailThreadRequest } = this.props;
    if (hashId) {
      sendGetEmailThreadRequest({
        hashId,
      });
    }
  }

  getEditorInstance(editor) {
    this.editorRef.current = editor;
    const { showReplyEditor } = this.props;
    if (showReplyEditor) {
      this.editorRef.current?.focus();
    }
  }

  showTemplateModal = () => {
    this.setState({
      showTemplates: true,
    });
  };

  hideTemplateModal = () => {
    this.setState({
      showTemplates: false,
    });
  };

  showScheduleModal = () => {
    const { isDemoAccount } = this.props;
    if (isDemoAccount) {
      this.setState({
        showGlobalRestrictionModal: true,
        globalRestrictionModalContent: {
          title: 'Scheduling replies is disabled in demo mode',
          description:
            'This is a demo account. Signup to schedule replies and manage conversations in your Unified Inbox.',
        },
      });
      return;
    }

    this.setState({
      showScheduleModal: true,
    });
  };

  hideScheduleModal = () => {
    this.setState({
      showScheduleModal: false,
    });
  };

  hideGlobalRestrictionModal() {
    this.setState({ showGlobalRestrictionModal: false });
  }

  // Saving Draft after debounce is completed and conditions are met.
  sendDraftSave() {
    const {
      selectedEmail,
      unifiedSchedule,
      replyDraftRequestStatus,
      sendReplyDraftRequest,
    } = this.props;
    const { values, errors, temporaryInputValues } = this.state;

    // Checking validation for sending draft request
    const isError = getIsErrorInReplyForwardEditor({
      errors,
      values,
      temporaryInputValues,
      validate: this.validate,
    });
    if (isError) return;

    // Check if request is already pending
    if (replyDraftRequestStatus === RequestStatus.Pending) {
      this.setState({ isDraftSavePending: true });
      return;
    }

    const {
      values: { to, cc, bcc, content },
      selectedTemplate,
      attachments,
    } = this.state;

    const draftAttachments = attachments
      .filter((item) => item.status === AttachmentUploadStatus.UploadSuccess)
      .map(
        (item): UnifiedScheduleAttachment => ({
          id: item.attachmentId,
          name: item.file.name,
          size: String(item.file.size),
          type: item.file.type,
        }),
      );

    const replyDraftPayload: ReplyDraftPayload = {
      ...(unifiedSchedule?.unifiedScheduledId && {
        unifiedScheduledId: unifiedSchedule?.unifiedScheduledId,
      }),
      emailThreadId: Number(selectedEmail.emailThreadId),
      repliedOnEmailId: Math.abs(Number(selectedEmail.emailId)),
      content: stripChipTagsFromHtml(content),
      to,
      cc,
      bcc,
      attachments: draftAttachments,
      templateId: selectedTemplate ? Number(selectedTemplate.id) : null,
    };
    sendReplyDraftRequest(replyDraftPayload);
  }

  addMergeTagInContent(option) {
    const { tag } = option;
    const uuid = uuidv4();

    let formattedTag = tag;
    const lastPipeIndex = tag.lastIndexOf('|');

    if (lastPipeIndex !== -1) {
      const beforePipe = tag.slice(0, lastPipeIndex + 1);
      const afterPipe = tag.slice(lastPipeIndex + 1);

      formattedTag =
        beforePipe + (isValidHTML(afterPipe) ? afterPipe : nl2br(afterPipe));
    }

    this.editorRef.current?.insertContent(
      insertChipForMergeTag(formattedTag, uuid, false, true, option?.fieldType),
    );
    setCaretAfterChip(this.editorRef.current, uuid);
  }

  addVariableTagInContent(tag: string) {
    // this.editorRef?.current?.insertContent(tag);

    const uuid = uuidv4();
    if (dateChipTags(tag)) {
      this.editorRef.current?.insertContent(
        insertChipForDateTag(tag, uuid, true),
      );
    } else {
      this.editorRef.current?.insertContent(
        insertChipForMergeTag(tag, uuid, true),
      );
    }
    // Move caret to after the inserted chip
    setCaretAfterChip(this.editorRef.current, uuid);
  }

  validate(name: string, value: string): string {
    const { t, sequenceId } = this.props;
    const { attachmentsSizeInBytes, attachments, values } = this.state;

    return rawValidate({
      name,
      value,
      allowEmptySubject: true,
      showTitle: false,
      attachmentsSizeInBytes,
      attachments,
      t,
      noMergeAndVariableTagsAllowed: values.to?.length > 1,
      isExternalEmail: !sequenceId,
    });
  }

  render() {
    const {
      label,
      action,
      selectedEmail,
      unifiedSchedule,

      // Inline Image Upload
      sendImageUploadRequest,

      // Upload Attachment
      sendAttachmentUploadRequest,
      uploadAttachmentRequestStatus,
      uploadAttachmentRequestError,
      uploadAttachmentRequestMessage,
      uploadedAttachment,
      resetUploadAttachmentRequest,

      // Delete Attachment
      sendDeleteAttachmentRequest,
      deleteAttachmentRequestStatus,
      deleteAttachmentRequestError,
      deletedAttachmentId,
      resetDeleteAttachmentRequest,

      replyEmailRequestStatus,
      forwardEmailRequestStatus,
      replyScheduleRequestStatus,
      replyDraftRequestStatus,
      replyDraftDiscardRequestStatus,

      fields,
      userTimezone,
      sequenceId,
      shouldFetchEmailThreadsOnReplyAndForward = true,
      showSubject = false,
      subjectValue = '',
      isEmailAccountDeleted = false,
      replyDraftRequestError,
    } = this.props;

    const {
      values,
      errors,
      temporaryInputValues,
      isCCInputVisible,
      isBCCInputVisible,
      showTemplates,
      showConfirmationModal,
      attachments,
      showScheduleModal,
      isDraftSavedTextVisible,
      isEditorContentUnsaved,
      showGlobalRestrictionModal,
      globalRestrictionModalContent,
      isTemplateApplied,
    } = this.state;

    const mergeTagOptions = prepareMergeTagOptions(fields);

    const draftRejectedForDeletedAccount =
      !isEmailAccountDeleted &&
      !!replyDraftRequestError?.message &&
      replyDraftRequestError.message
        .toLowerCase()
        .includes('email account has been deleted');

    const isReadOnly = isEmailAccountDeleted;

    return (
      <div className="reply-forward-editor">
        <div className="d-flex justify-content-between align-items-center">
          {unifiedSchedule?.unifiedScheduledId ? (
            <div className="reply-forward-editor--label-draft">Draft</div>
          ) : (
            <div className="reply-forward-editor--label">{label}</div>
          )}
          {isDraftSavedTextVisible ? (
            <div className="reply-forward-editor--label">Draft Auto Saved</div>
          ) : null}
        </div>
        {draftRejectedForDeletedAccount && (
          <div className="reply-forward-editor--deleted-banner">
            Email account was deleted. Your changes can&apos;t be saved.
          </div>
        )}
        <div
          className={`reply-forward-editor--container${
            isReadOnly ? ' reply-forward-editor--container--read-only' : ''
          }`}
          aria-disabled={isReadOnly}>
          <EmailInput
            name="to"
            label="To:"
            value={temporaryInputValues.to}
            emails={values.to}
            onChange={this.onInputChange}
            onValueChange={this.onTempInputChange}
            isEmailsRemovable={true}
            nonRemovableEmails={
              this.defaultToInputValue?.length > 0
                ? this.defaultToInputValue[0]
                : []
            }
            showCCInput
            showBCCInput
            isCCInputVisible={isCCInputVisible}
            isBCCInputVisible={isBCCInputVisible}
            onCCInputToggle={this.onCCInputToggle}
            onBCCInputToggle={this.onBCCInputToggle}
          />
          {isCCInputVisible && (
            <EmailInput
              name="cc"
              label="Cc:"
              value={temporaryInputValues.cc}
              emails={values.cc}
              onChange={this.onInputChange}
              onValueChange={this.onTempInputChange}
              isEmailsRemovable={true}
            />
          )}
          {isBCCInputVisible && (
            <EmailInput
              name="bcc"
              label="Bcc:"
              value={temporaryInputValues.bcc}
              emails={values.bcc}
              onChange={this.onInputChange}
              onValueChange={this.onTempInputChange}
              isEmailsRemovable={true}
            />
          )}

          {showSubject && (
            <SingleEditorInput
              inputValue={subjectValue}
              enableEmoji
              enableMergeTag={false}
              enableSpintax={false}
              placeholder="Enter a Subject .."
              showDivider={false}
              showTitle="Subject:"
              disableEditMode={action === 'reply'}
              onInputChange={undefined}
            />
          )}

          <div className="reply-forward-editor--editor-wrapper">
            <Editor
              ref={this.editorRef}
              getEditorInstance={this.getEditorInstance}
              value={values.content}
              onEditorChange={this.onEditorChange}
              error={!!errors.content}
              errorMessage={errors.content}
              imageUploader={({ formData, onUploadProgress }) =>
                sendImageUploadRequest(formData, onUploadProgress)
              }
              shouldRenderTemplate={!!sequenceId}
              showTemplateModal={this.showTemplateModal}
              mergeTags={mergeTagOptions}
              addMergeTagInContent={this.addMergeTagInContent}
              addVariableTagInContent={this.addVariableTagInContent}
              // Attachments
              showAddAttachments={true}
              attachments={attachments}
              onAttachmentStatusChange={this.onAttachmentStatusChange}
              onAttachmentsChange={this.onAttachmentsChange}
              sendAttachmentUploadRequest={sendAttachmentUploadRequest}
              uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
              uploadAttachmentRequestError={uploadAttachmentRequestError}
              uploadAttachmentRequestMessage={uploadAttachmentRequestMessage}
              uploadedAttachment={uploadedAttachment}
              sendDeleteAttachmentRequest={sendDeleteAttachmentRequest}
              deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
              deleteAttachmentRequestError={deleteAttachmentRequestError}
              deletedAttachmentId={deletedAttachmentId}
              resetUploadAttachmentRequest={resetUploadAttachmentRequest}
              resetDeleteAttachmentRequest={resetDeleteAttachmentRequest}
              onAttachmentsMetaChange={this.onAttachmentsMetaChange}
              showMergeAndVariableTags={!!sequenceId}
              tagsDropdownClassName="reply-forward-editor-tags-dropdown"
              attachmentsClassname="reply-forward-editor-attachement"
              onEditorBlur={this.onEditorBlur}
              uniqueId={selectedEmail?.emailId}
              action={action}
              onTemplateApplied={isTemplateApplied}
              onTemplateAppliedReset={this.onTemplateAppliedReset}
            />

            <TemplateModal
              show={showTemplates}
              onClose={this.hideTemplateModal}
              onTemplateChange={this.onTemplateChange}
            />
          </div>

          <div className="reply-forward-editor--footer">
            <Button
              variant="tertiary"
              onClick={this.onDiscardModalShow}
              disabled={
                getIsRequestPending(replyEmailRequestStatus) ||
                getIsRequestPending(forwardEmailRequestStatus) ||
                ((isEditorContentUnsaved ||
                  getIsRequestPending(replyDraftRequestStatus)) &&
                  !unifiedSchedule)
              }
            >
              Discard
            </Button>

            {action !== 'forward' &&
              shouldFetchEmailThreadsOnReplyAndForward &&
              (() => {
                const scheduleBtn = (
                  <Button
                    variant="secondary"
                    onClick={this.showScheduleModal}
                    disabled={
                      getIsErrorInReplyForwardEditor({
                        errors,
                        values,
                        temporaryInputValues,
                        validate: this.validate,
                      }) ||
                      !unifiedSchedule ||
                      getIsRequestPending(replyEmailRequestStatus) ||
                      isEmailAccountDeleted
                    }
                  >
                    Schedule
                  </Button>
                );
                return isEmailAccountDeleted ? (
                  <OverlayTooltip
                    text="The email account used to send this email has been deleted."
                    placement={Placement.Top}
                  >
                    {scheduleBtn}
                  </OverlayTooltip>
                ) : (
                  scheduleBtn
                );
              })()}
            {(() => {
              const sendBtn = (
                <Button
                  onClick={this.onEmailSend}
                  isLoading={
                    getIsRequestPending(replyEmailRequestStatus) ||
                    getIsRequestPending(forwardEmailRequestStatus)
                  }
                  loadingText="Sending..."
                  disabled={
                    getIsErrorInReplyForwardEditor({
                      errors,
                      values,
                      temporaryInputValues,
                      validate: this.validate,
                    }) ||
                    (!unifiedSchedule && action !== 'forward') ||
                    isEmailAccountDeleted
                  }
                >
                  Send
                </Button>
              );
              return isEmailAccountDeleted ? (
                <OverlayTooltip
                  text="The email account used to send this email has been deleted."
                  placement={Placement.Top}
                >
                  {sendBtn}
                </OverlayTooltip>
              ) : (
                sendBtn
              );
            })()}
          </div>
        </div>

        <GlobalRestrictionModal
          show={showGlobalRestrictionModal}
          onClose={this.hideGlobalRestrictionModal}
          title={globalRestrictionModalContent.title}
          content={globalRestrictionModalContent.description}
          primaryBtnLabel="Sign up"
        />

        <ScheduleReplyModal
          show={showScheduleModal}
          onClose={this.hideScheduleModal}
          onSchedule={this.onSchedule}
          userTimezone={userTimezone}
          emailId={selectedEmail.emailId}
          replyScheduleRequestStatus={replyScheduleRequestStatus}
          sequenceId={sequenceId}
          unifiedScheduledId={unifiedSchedule?.unifiedScheduledId}
        />

        <ConfirmationModalV3
          show={showConfirmationModal}
          title={
            shouldFetchEmailThreadsOnReplyAndForward
              ? 'Discard Draft'
              : 'Discard Changes'
          }
          className={`schedule-confirmation-popup ${
            shouldFetchEmailThreadsOnReplyAndForward
              ? ''
              : 'schedule-confirmation-popup-email-tab-prospect-drawer'
          }`}
          iconType={ConfirmationModalIconType.TRASH_RED}
          contents={[
            `${
              shouldFetchEmailThreadsOnReplyAndForward
                ? 'All your changes in this email draft will be lost, if you discard.'
                : 'Your email is already in draft version, if you continue; this draft will be discarded.'
            }`,
            `${
              shouldFetchEmailThreadsOnReplyAndForward
                ? 'Do you want to continue?'
                : 'Are you sure you want to discard the changes?'
            }`,
          ]}
          showCloseIcon={false}
          onClose={() => this.setState({ showConfirmationModal: false })}
          onSubmit={this.onDiscardDraft}
          submitButtonVariant={
            shouldFetchEmailThreadsOnReplyAndForward
              ? Variant.Danger
              : Variant.Primary
          }
          submitButtonClassName="delete-btn"
          submitButtonText={
            shouldFetchEmailThreadsOnReplyAndForward ? 'Discard' : 'Confirm'
          }
          isSubmitLoading={getIsRequestPending(replyDraftDiscardRequestStatus)}
          isSubmitDisabled={getIsRequestPending(replyDraftDiscardRequestStatus)}
          isCancelDisabled={getIsRequestPending(replyDraftDiscardRequestStatus)}
          cancelButtonText="Cancel"
        />
      </div>
    );
  }
}

export default withTranslation()(ReplyForwardEditor);
